import { useNotification } from 'app/providers'
import { Skeleton } from 'antd'
import { Button } from 'common/components/Button/Button'
import { Modal } from 'common/components/Modal'
import {
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import {
  useFetchPhotoStreamQuery,
  useUpdateEncounterPhotosMutation,
} from 'features/Procedure/state/procedure.api'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { retryEncounterPhotoUpdates, showPersistentPhotoError } from './photos.helpers'
import {
  addPhotoActionBreadcrumb,
  capturePhotoActionError,
  capturePhotoActionMessage,
} from './photos.sentry'
import styles from './photoPreviewModal.module.scss'

type PhotoCategory = 'before' | 'after'

interface PhotoPreviewModalProps {
  open: boolean
  photoId: number | null
  category: PhotoCategory | null
  onClose: () => void
  onDelete?: () => void
  onRetake?: () => void
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  readOnly?: boolean
}

export const PhotoPreviewModal: FC<PhotoPreviewModalProps> = ({
  open,
  photoId,
  category,
  onClose,
  onDelete,
  onRetake,
  selectedPatientId,
  selectedEncounterUid,
  readOnly = false,
}) => {
  const { setNotification } = useNotification()
  const modalTitle =
    category === 'before'
      ? 'View Before Photo'
      : category === 'after'
      ? 'View After Photo'
      : 'Photo Preview'
  const { siteId } = useParams<{ siteId: string }>()

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [prevPhotoId, setPrevPhotoId] = useState<number | null>(null)
  const notifiedPreviewErrorPhotoIdRef = useRef<number | null>(null)
  const [updateEncounterPhotos] = useUpdateEncounterPhotosMutation()

  if (photoId !== prevPhotoId) {
    setPrevPhotoId(photoId)
    setImageUrl(null)
    setIsImageLoaded(false)
  }

  const {
    data: imageBlob,
    isFetching,
    isError,
    error: previewError,
  } = useFetchPhotoStreamQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      documentId: photoId!,
    },
    {
      skip: !open || !siteId || !selectedPatientId || !photoId,
    },
  )

  useEffect(() => {
    if (!open || !(imageBlob instanceof Blob)) {
      setImageUrl(null)
      setIsImageLoaded(false)
      return
    }

    const url = URL.createObjectURL(imageBlob)
    setImageUrl(url)
    setIsImageLoaded(false)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [open, photoId, imageBlob])

  useEffect(() => {
    if (!open) {
      setIsImageLoaded(false)
      setIsDeleting(false)
      notifiedPreviewErrorPhotoIdRef.current = null
    }
  }, [open])

  useEffect(() => {
    if (!open || !photoId || !isError) {
      if (!isError) {
        notifiedPreviewErrorPhotoIdRef.current = null
      }
      return
    }

    if (notifiedPreviewErrorPhotoIdRef.current === photoId) {
      return
    }

    notifiedPreviewErrorPhotoIdRef.current = photoId
    showPersistentPhotoError(
      setNotification,
      'Photo preview failed to load',
      previewError,
      'Failed to load photo preview.',
    )
  }, [isError, open, photoId, previewError, setNotification])

  const handleImageLoad = () => {
    setIsImageLoaded(true)
  }

  const handleImageError = () => {
    setIsImageLoaded(false)

    if (!photoId || notifiedPreviewErrorPhotoIdRef.current === photoId) {
      return
    }

    notifiedPreviewErrorPhotoIdRef.current = photoId
    showPersistentPhotoError(
      setNotification,
      'Photo preview failed to load',
      previewError || new Error('Failed to load photo preview.'),
      'Failed to load photo preview.',
    )
  }

  const buildPhotoActionContext = () => ({
    category,
    patientId: selectedPatientId,
    encounterUid: selectedEncounterUid,
    siteId,
    photoId,
  })

  const handleDelete = async () => {
    const sentryContext = buildPhotoActionContext()

    if (!photoId || !siteId || !selectedPatientId || !selectedEncounterUid) {
      capturePhotoActionMessage(
        'Delete photo aborted: missing required fields',
        sentryContext,
        {
          source: 'photo-preview-modal',
          missingFields: [
            !photoId && 'photoId',
            !siteId && 'siteId',
            !selectedPatientId && 'selectedPatientId',
            !selectedEncounterUid && 'selectedEncounterUid',
          ].filter(Boolean),
        },
        'warning',
      )
      return
    }

    capturePhotoActionMessage('Delete photo requested', sentryContext, {
      source: 'photo-preview-modal',
    })
    setIsDeleting(true)
    try {
      addPhotoActionBreadcrumb('Delete photo API call started', sentryContext, {
        source: 'photo-preview-modal',
      })
      await retryEncounterPhotoUpdates({
        execute: (files) =>
          updateEncounterPhotos({
            siteId,
            patientId: selectedPatientId,
            encounterUid: selectedEncounterUid,
            files,
          }).unwrap(),
        files: [
          {
            file_id: photoId,
            delete: 1,
          },
        ],
        fallbackMessage: 'Failed to delete photo.',
      })
      capturePhotoActionMessage('Delete photo completed', sentryContext, {
        source: 'photo-preview-modal',
      })
      onDelete?.()
      onClose()
    } catch (error) {
      capturePhotoActionError(error, sentryContext, {
        source: 'photo-preview-modal',
        phase: 'handleDelete',
      })
      showPersistentPhotoError(setNotification, 'Delete failed', error, 'Failed to delete photo.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRetake = () => {
    capturePhotoActionMessage('Retake requested from photo preview', buildPhotoActionContext(), {
      source: 'photo-preview-modal',
    })
    onClose()
    onRetake?.()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={modalTitle}
      keyboard
      footer={
        !readOnly && (
          <>
            <Button
              modifier={BUTTON_MODIFIER.SECONDARY}
              severity={BUTTON_SEVERITY.DANGER}
              proportion={BUTTON_PROPORTION.MEDIUM}
              onClick={handleDelete}
              disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              modifier={BUTTON_MODIFIER.PRIMARY}
              proportion={BUTTON_PROPORTION.MEDIUM}
              onClick={handleRetake}
              disabled={isDeleting}>
              Retake
            </Button>
          </>
        )
      }
      width={500}
      className={styles.previewModal}>
      <div className={styles.previewContent}>
        {(isFetching || !isImageLoaded) && (
          <div className={styles.skeletonContainer}>
            <Skeleton.Image active className={styles.skeleton} />
          </div>
        )}
        {imageUrl && (
          <img
            src={imageUrl}
            alt='Preview'
            className={styles.previewImage}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isImageLoaded ? 'block' : 'none' }}
          />
        )}
        {!isFetching && !imageUrl && isError && (
          <div className={styles.error}>Failed to load image</div>
        )}
      </div>
    </Modal>
  )
}
